/**
 * Clear Insurance Action
 *
 * Synchronous action that clears insurance data from context.
 * This is an ACTION (not an actor) - fire-and-forget, no async.
 */

import { assign } from "xstate";
import type { ActionWithMetadata } from "./index";

/**
 * Clear Insurance Action
 *
 * Clears insurance-related data from machine context while saving to background fields.
 * Used when user skips OTP to clear insurance for fresh input, while preserving
 * original insurance data in background fields for duplicate detection.
 *
 * Similar to clearUserIds which saves userId/consumerId to userIdBackground/consumerIdBackground,
 * this saves insurance data to insurancesBackground so that createInsurance actor can
 * detect if user enters the same insurance and skip creation.
 *
 * Example:
 * ```json
 * {
 *   "event": "SKIP",
 *   "actions": [{ "type": "clearInsurance" }],
 *   "target": "nextState"
 * }
 * ```
 */
export const clearInsurance: ActionWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
  impl: assign(({ context }: { context: any }) => ({
    // Clear active insurance fields
    insurances: undefined,
    primaryInsurance: undefined,
    secondaryInsurance: undefined,
    hasSecondaryInsurance: undefined,
    insuranceStates: undefined,
    insuranceInputSnapshot: undefined,
    // Save to background fields for duplicate detection in createInsurance actor
    insurancesBackground: context.insurances,
    primaryInsuranceBackground: context.insurances?.primaryInsurance,
    secondaryInsuranceBackground: context.insurances?.secondaryInsurance,
  })),
  metadata: {
    displayName: "clearInsurance",
    description:
      "Clear insurance data from context, saving to background for duplicate detection",
    category: "context",
  },
};

/**
 * Clear Secondary Insurance Action
 *
 * Clears secondary insurance data while preserving primary insurance.
 * Used when user says "No" to secondary insurance question after previously
 * having added secondary insurance.
 *
 * Example:
 * ```json
 * {
 *   "event": "SUBMIT",
 *   "guard": "!hasSecondaryInsurance",
 *   "actions": [{ "type": "clearSecondaryInsurance" }],
 *   "target": "verifyingInsurance"
 * }
 * ```
 */
export const clearSecondaryInsurance: ActionWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
  impl: assign(({ context }: { context: any }) => {
    const insurances = context.insurances;
    // Remove secondaryInsurance from insurances object, keeping primaryInsurance
    const updatedInsurances = insurances
      ? { primaryInsurance: insurances.primaryInsurance }
      : undefined;

    const insuranceStates = context.insuranceStates;
    const updatedInsuranceStates = insuranceStates?.primary
      ? { primary: insuranceStates.primary }
      : insuranceStates;

    return {
      insurances: updatedInsurances,
      insuranceStates: updatedInsuranceStates,
      secondaryInsurance: undefined,
      hasSecondaryInsurance: false,
      secondaryInsurancePhotoFront: undefined,
      secondaryInsurancePhotoBack: undefined,
      ocrSecondaryInsurance: undefined,
    };
  }),
  metadata: {
    displayName: "clearSecondaryInsurance",
    description:
      "Clear secondary insurance data from context, preserving primary insurance",
    category: "context",
  },
};

/**
 * Clear Insured Pricing Action
 *
 * Clears INSURED price data from pricingMap while preserving CASH prices.
 * Used when insurance inputs change to force re-pricing with new insurance.
 *
 * Example:
 * ```json
 * {
 *   "event": "SUBMIT_INSURANCE",
 *   "actions": [{ "type": "clearInsuredPricing" }],
 *   "target": "verifyingInsurance"
 * }
 * ```
 */
/**
 * Helper to check if two insurances match by key fields (payer + memberId + state)
 */
function insurancesMatch(
  entered: { payer?: string; memberId?: string; state?: string } | undefined,
  background:
    | { payer?: string; memberId?: string; state?: string }
    | undefined
    | null,
): boolean {
  if (!entered || !background) return false;

  const enteredPayer = entered.payer?.toLowerCase().trim();
  const backgroundPayer = background.payer?.toLowerCase().trim();
  const enteredMemberId = entered.memberId?.toLowerCase().trim();
  const backgroundMemberId = background.memberId?.toLowerCase().trim();
  const enteredState = entered.state?.toLowerCase().trim();
  const backgroundState = background.state?.toLowerCase().trim();

  return (
    enteredPayer === backgroundPayer &&
    enteredMemberId === backgroundMemberId &&
    enteredState === backgroundState
  );
}

/**
 * Restore Insurance IDs from Background Action
 *
 * When user skips OTP and enters insurance that matches their existing insurance,
 * this action restores the insuranceId from background fields.
 *
 * This is needed for:
 * 1. Pricing - insuranceId is used to associate pricing with existing insurance records
 * 2. Create insurance - having insuranceId prevents duplicate creation
 *
 * Should run after insurance input is saved, before verification/pricing.
 *
 * Example:
 * ```json
 * {
 *   "event": "SUBMIT",
 *   "actions": [{ "type": "saveFormData" }, { "type": "restoreInsuranceIdsFromBackground" }],
 *   "target": "verifyingInsurance"
 * }
 * ```
 */
export const restoreInsuranceIdsFromBackground: ActionWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
  impl: assign(({ context }: { context: any }) => {
    const insurances = context.insurances;
    const primaryBackground = context.primaryInsuranceBackground;
    const secondaryBackground = context.secondaryInsuranceBackground;

    if (!insurances) return {};

    const updates: Record<string, unknown> = {};
    let updatedInsurances = { ...insurances };

    // Check primary insurance
    if (
      insurances.primaryInsurance &&
      !insurances.primaryInsurance.insuranceId &&
      insurancesMatch(insurances.primaryInsurance, primaryBackground)
    ) {
      updatedInsurances = {
        ...updatedInsurances,
        primaryInsurance: {
          ...insurances.primaryInsurance,
          insuranceId: primaryBackground?.insuranceId,
          ehrInsuranceId: primaryBackground?.ehrInsuranceId,
          ehrInsurancePackageId: primaryBackground?.ehrInsurancePackageId,
        },
      };
    }

    // Check secondary insurance
    if (
      insurances.secondaryInsurance &&
      !insurances.secondaryInsurance.insuranceId &&
      insurancesMatch(insurances.secondaryInsurance, secondaryBackground)
    ) {
      updatedInsurances = {
        ...updatedInsurances,
        secondaryInsurance: {
          ...insurances.secondaryInsurance,
          insuranceId: secondaryBackground?.insuranceId,
          ehrInsuranceId: secondaryBackground?.ehrInsuranceId,
          ehrInsurancePackageId: secondaryBackground?.ehrInsurancePackageId,
        },
      };
    }

    // Only return updates if something changed
    if (updatedInsurances !== insurances) {
      updates.insurances = updatedInsurances;
    }

    return updates;
  }),
  metadata: {
    displayName: "restoreInsuranceIdsFromBackground",
    description:
      "Restore insuranceId from background when entered insurance matches existing",
    category: "context",
  },
};

export const clearInsuredPricing: ActionWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
  impl: assign(({ context }: { context: any }) => {
    const pricingMap = context.pricingMap;
    if (!pricingMap || typeof pricingMap !== "object") {
      return {};
    }

    // Create new pricingMap with INSURED prices cleared and mode reset to CASH
    // Both must be updated together to maintain consistency — clearing priceData.INSURED
    // while leaving mode as "INSURED" causes crashes when the actor reads mode before
    // the storefront pricing query re-fetches.
    const clearedPricingMap = Object.fromEntries(
      Object.entries(pricingMap).map(([hrtId, entry]) => {
        const typedEntry = entry as {
          priceData?: Record<string, unknown>;
          mode?: string;
        };
        if (typedEntry?.priceData) {
          return [
            hrtId,
            {
              ...typedEntry,
              mode: "CASH",
              priceData: { ...typedEntry.priceData, INSURED: undefined },
            },
          ];
        }
        return [hrtId, entry];
      }),
    );

    return { pricingMap: clearedPricingMap };
  }),
  metadata: {
    displayName: "clearInsuredPricing",
    description:
      "Clear INSURED price data from pricingMap, preserving CASH prices",
    category: "context",
  },
};
